// GuestRSVP — Phase I of the Client-Surfaces Programme.
// Public no-auth page reached by a tokenised magic-link
//   /guest/rsvp/:token
// Reads the public event card from /api/public/events/{token} and posts the RSVP
// to /api/public/events/{token}/rsvp. No login required. Operator-brandable later (Phase J).
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PartyPopper, CheckCircle2, Loader2, AlertTriangle, Calendar, MapPin, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GuestRSVP = () => {
  const { token } = useParams();
  const [evt, setEvt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [rsvp, setRsvp] = useState(null);                // 'attending' | 'declined'
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`${API_URL}/api/public/events/${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.detail || r.statusText)))
      .then(data => { if (!cancelled) setEvt(data); })
      .catch(err => { if (!cancelled) setError(String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rsvp || !name) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/events/${encodeURIComponent(token)}/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, party_size: partySize, response: rsvp, notes }),
      });
      if (res.ok) setDone(await res.json());
      else {
        const err = await res.json().catch(() => ({}));
        setDone({ error: err.detail || res.statusText });
      }
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-muted grid place-items-center" data-testid="guest-rsvp-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !evt) {
    return (
      <div className="min-h-screen bg-surface-muted grid place-items-center px-4" data-testid="guest-rsvp-error">
        <div className="bg-surface border border-surface-border rounded-lg p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-warn mb-3" />
          <h1 className="font-display text-xl text-ink">Invitation link not found</h1>
          <p className="text-sm text-ink-muted mt-2">
            {error || 'The link may have expired or already been used.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="guest-rsvp-page">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-xs uppercase tracking-overline text-accent-500 flex items-center gap-1.5">
          <PartyPopper className="w-3.5 h-3.5" /> You’re invited
        </div>
        <h1 className="font-display text-3xl text-ink mt-1">{evt.title || evt.name}</h1>
        {evt.host_name ? <p className="text-sm text-ink-muted mt-1">Hosted by {evt.host_name}</p> : null}

        <div className="mt-6 bg-surface border border-surface-border rounded-lg p-5 space-y-2 text-sm">
          {evt.date ? <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-ink-muted" /> {new Date(evt.date).toLocaleString()}</div> : null}
          {evt.location ? <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-ink-muted" /> {evt.location}</div> : null}
          {typeof evt.capacity === 'number' ? <div className="flex items-center gap-2 text-ink-muted text-xs"><Users className="w-3.5 h-3.5" /> Capacity: {evt.capacity}</div> : null}
          {evt.description ? <p className="text-ink mt-3 whitespace-pre-line">{evt.description}</p> : null}
        </div>

        {done && !done.error ? (
          <div className="mt-6 bg-ok/5 border border-ok/30 rounded-lg p-5 text-center" data-testid="guest-rsvp-success">
            <CheckCircle2 className="w-8 h-8 mx-auto text-ok mb-2" />
            <p className="font-display text-lg text-ink">Thanks, {name}.</p>
            <p className="text-sm text-ink-muted mt-1">
              You’re <strong>{rsvp === 'attending' ? 'attending' : 'not attending'}</strong>. We’ve sent a confirmation to {email || 'your inbox'}.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 bg-surface border border-surface-border rounded-lg p-5 space-y-4" data-testid="guest-rsvp-form">
            <div>
              <label className="text-xs uppercase tracking-overline text-ink-muted">Your name</label>
              <input aria-label="Your name"
                type="text" required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                data-testid="guest-rsvp-name"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-overline text-ink-muted">Email (optional)</label>
                <input aria-label="Email (optional)"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                  data-testid="guest-rsvp-email"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-overline text-ink-muted">Party size</label>
                <input aria-label="Party size"
                  type="number" min="1" max="20" value={partySize}
                  onChange={(e) => setPartySize(parseInt(e.target.value || '1', 10))}
                  className="mt-1 w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm font-data tabular-nums focus:outline-none focus:border-primary-500"
                  data-testid="guest-rsvp-party-size"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-overline text-ink-muted">Notes (allergies, special requests)</label>
              <textarea aria-label="Notes (allergies, special requests)"
                value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                className="mt-1 w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                data-testid="guest-rsvp-notes"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button" onClick={() => setRsvp('attending')}
                className={`px-4 py-3 rounded-md border motion-organic ${
                  rsvp === 'attending' ? 'bg-primary-500 text-surface border-primary-500' : 'bg-surface border-surface-border text-ink hover:bg-primary-50'
                }`}
                data-testid="guest-rsvp-attending"
              >
                <CheckCircle2 className="w-4 h-4 inline mr-1.5" /> I’ll be there
              </button>
              <button
                type="button" onClick={() => setRsvp('declined')}
                className={`px-4 py-3 rounded-md border motion-organic ${
                  rsvp === 'declined' ? 'bg-ink text-surface border-ink' : 'bg-surface border-surface-border text-ink hover:bg-surface-sunken'
                }`}
                data-testid="guest-rsvp-declined"
              >
                Can’t make it
              </button>
            </div>

            <button
              type="submit" disabled={!rsvp || !name || submitting}
              className="w-full px-4 py-3 bg-accent-500 text-ink rounded-md text-sm font-semibold hover:bg-accent-600 disabled:opacity-40 motion-organic"
              data-testid="guest-rsvp-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> : null}
              Send RSVP
            </button>

            {done?.error ? (
              <p className="text-xs text-danger" data-testid="guest-rsvp-error-banner">
                {done.error}
              </p>
            ) : null}
          </form>
        )}

        <p className="text-xs text-ink-muted text-center mt-8">Powered by Smart F&amp;B·OS</p>
      </div>
    </div>
  );
};

export default GuestRSVP;
